import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { useToast } from "vue-toastification";
  import {
    Armchair,
    Bath,
    BedDouble,
    Car,
    Home,
    MoreVertical,
    MoveHorizontal,
    Pencil,
    Plus,
    Trash2,
  } from "lucide-vue-next";
  import LandSubdivisionPackageModal from "@/v2/components/ui/modals/LandSubdivisionPackageModal.vue";
  import LandSubdivisionPackageDeleteModal from "@/v2/components/ui/modals/LandSubdivisionPackageDeleteModal.vue";
  import landSubdivisionPackageApi from "@/services/api/landSubdivisionPackageApi";
  import stageApi from "@/services/api/stageApi";
  import lotApi from "@/services/api/lotApi";
  import {
    LandSubdivisionPackage,
    LandSubdivisionPackagePayload,
  } from "@/types/LandSubdivisionPackage";
  import { EstateInterface } from "@/types/EstateInterface";
  import { EstateAddon } from "@/enums/EstateAddon";
  import { EstateType } from "@/enums/EstateType";
  import { formatPrice } from "@/helpers/transformation/price";

  interface StageOption {
    _id: string;
    title: string;
  }
  interface LotOption {
    _id: string;
    title: string;
    stageId: string | { _id: string };
  }
  interface SaveEvent {
    payload: LandSubdivisionPackagePayload;
    logoFile: File | null;
    heroFile: File | null;
    brochureFile: File | null;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandSubdivisionPackagesView',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const router = useRouter();
  const toast = useToast();
  const packages = ref<LandSubdivisionPackage[]>([]);
  const stages = ref<StageOption[]>([]);
  const lots = ref<LotOption[]>([]);
  const loading = ref(false);
  const saving = ref(false);
  const showModal = ref(false);
  const editingPackage = ref<LandSubdivisionPackage | null>(null);
  const packageToDelete = ref<LandSubdivisionPackage | null>(null);
  const deletingPackage = ref(false);
  const openMenuId = ref("");

  const estateId = computed<string>(() => store.state.user.selectedEstateId);
  const selectedEstate = computed<EstateInterface | undefined>(() =>
    store.state.user.landownerEstates.find(
      (estate: EstateInterface) => estate._id === estateId.value
    )
  );
  const hasFeature = computed(
    () =>
      selectedEstate.value?.type === EstateType.LAND_SUBDIVISION &&
      selectedEstate.value?.addons?.includes(EstateAddon.HL_PACKAGES)
  );

  async function fetchData() {
    if (!estateId.value || !selectedEstate.value) return;

    if (!hasFeature.value) {
      router.replace("/v2/lots");
      return;
    }

    loading.value = true;

    try {
      const [packageResponse, stageResponse, lotResponse] = await Promise.all([
        landSubdivisionPackageApi.getByEstateId(estateId.value),
        stageApi.getStages({ estateId: estateId.value }),
        lotApi.getLots({ estateId: estateId.value }),
      ]);

      packages.value = packageResponse.data;
      stages.value = stageResponse.data;
      lots.value = lotResponse.data;
    } finally {
      loading.value = false;
    }
  }

  onMounted(() => {
    fetchData();
    document.addEventListener("click", closeOpenMenu);
  });
  onBeforeUnmount(() => {
    document.removeEventListener("click", closeOpenMenu);
  });
  watch([estateId, selectedEstate, hasFeature], () => {
    if (selectedEstate.value && !hasFeature.value) {
      router.replace("/v2/lots");
      return;
    }

    fetchData();
  });

  function openCreate() {
    editingPackage.value = null;
    showModal.value = true;
  }

  function openEdit(packageRecord: LandSubdivisionPackage) {
    editingPackage.value = packageRecord;
    openMenuId.value = "";
    showModal.value = true;
  }

  function closeModal() {
    showModal.value = false;
    editingPackage.value = null;
  }

  function toggleMenu(id: string) {
    openMenuId.value = openMenuId.value === id ? "" : id;
  }

  function closeOpenMenu() {
    openMenuId.value = "";
  }

  async function savePackage(data: SaveEvent) {
    if (!estateId.value) return;

    saving.value = true;

    try {
      const payload = { ...data.payload };

      if (data.logoFile) {
        const response = await landSubdivisionPackageApi.uploadAsset(
          estateId.value,
          "builder-logo",
          data.logoFile
        );

        payload.builderLogoUrl = response.data.fileUrl;
        payload.builderLogoFileName = response.data.fileName;
      }

      if (data.heroFile) {
        const response = await landSubdivisionPackageApi.uploadAsset(
          estateId.value,
          "hero-image",
          data.heroFile
        );

        payload.heroImageUrl = response.data.fileUrl;
        payload.heroImageFileName = response.data.fileName;
      }

      if (data.brochureFile) {
        const response = await landSubdivisionPackageApi.uploadAsset(
          estateId.value,
          "brochure",
          data.brochureFile
        );

        payload.brochureUrl = response.data.fileUrl;
        payload.brochureFileName = response.data.fileName;
      }

      if (editingPackage.value) {
        await landSubdivisionPackageApi.update(
          editingPackage.value._id,
          payload
        );
        toast.success("Package updated");
      } else {
        await landSubdivisionPackageApi.create(estateId.value, payload);
        toast.success("Package created");
      }

      closeModal();
      await fetchData();
    } finally {
      saving.value = false;
    }
  }

  function openDeleteModal(packageRecord: LandSubdivisionPackage) {
    openMenuId.value = "";
    packageToDelete.value = packageRecord;
  }

  function closeDeleteModal() {
    if (!deletingPackage.value) packageToDelete.value = null;
  }

  async function confirmDeletePackage() {
    if (!packageToDelete.value || deletingPackage.value) return;

    deletingPackage.value = true;

    try {
      await landSubdivisionPackageApi.delete(packageToDelete.value._id);
      toast.success("Package deleted");
      packageToDelete.value = null;
      await fetchData();
    } finally {
      deletingPackage.value = false;
    }
  }

  function getLotTitle(lotId: unknown): string {
    const normalizedId =
      typeof lotId === "object" && lotId !== null && "_id" in lotId
        ? String((lotId as { _id: unknown })._id)
        : String(lotId);

    return (
      lots.value.find((lot) => String(lot._id) === normalizedId)?.title ?? "—"
    );
  }

const __returned__ = { store, router, toast, packages, stages, lots, loading, saving, showModal, editingPackage, packageToDelete, deletingPackage, openMenuId, estateId, selectedEstate, hasFeature, fetchData, openCreate, openEdit, closeModal, toggleMenu, closeOpenMenu, savePackage, openDeleteModal, closeDeleteModal, confirmDeletePackage, getLotTitle, computed, onBeforeUnmount, onMounted, ref, watch, get useStore() { return useStore }, get useRouter() { return useRouter }, get useToast() { return useToast }, get Armchair() { return Armchair }, get Bath() { return Bath }, get BedDouble() { return BedDouble }, get Car() { return Car }, get Home() { return Home }, get MoreVertical() { return MoreVertical }, get MoveHorizontal() { return MoveHorizontal }, get Pencil() { return Pencil }, get Plus() { return Plus }, get Trash2() { return Trash2 }, LandSubdivisionPackageModal, LandSubdivisionPackageDeleteModal, get landSubdivisionPackageApi() { return landSubdivisionPackageApi }, get stageApi() { return stageApi }, get lotApi() { return lotApi }, get LandSubdivisionPackage() { return LandSubdivisionPackage }, get LandSubdivisionPackagePayload() { return LandSubdivisionPackagePayload }, get EstateInterface() { return EstateInterface }, get EstateAddon() { return EstateAddon }, get EstateType() { return EstateType }, get formatPrice() { return formatPrice } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})